import React from "react";
import moment from "moment";
import { toCommas } from "../../utils/utils";
import styles from "./InvoiceDetails.module.css";

const PAYMENT_METHODS = ["Cash", "Check", "Credit Card", "Bank Transfer", "Others"];

const PaymentHistory = ({
  paymentRecords = [],
  allowEditing = false,
  onUpdatePayment = async () => {},
}) => {
  const [editingId, setEditingId] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [savingId, setSavingId] = React.useState(null);

  const startEdit = (record) => {
    if (!allowEditing) return;
    setEditingId(record._id);
    setDraft({
      _id: record._id,
      amountPaid: record.amountPaid ?? "",
      paymentMethod: record.paymentMethod || "",
      note: record.note || "",
      paidBy: record.paidBy || "",
      datePaid: record.datePaid
        ? moment(record.datePaid).format("YYYY-MM-DD")
        : "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
    setSavingId(null);
  };

  const handleSave = async () => {
    if (!draft || !allowEditing) return;
    const original = paymentRecords.find((rec) => rec._id === draft._id);
    if (!original) {
      cancelEdit();
      return;
    }
    setSavingId(draft._id);
    try {
      await onUpdatePayment({
        ...original,
        amountPaid: Number(draft.amountPaid) || 0,
        paymentMethod: draft.paymentMethod,
        note: draft.note,
        paidBy: draft.paidBy,
        datePaid: draft.datePaid ? new Date(draft.datePaid).toISOString() : null,
      });
      cancelEdit();
    } catch (err) {
      console.error(err);
      setSavingId(null);
    }
  };

  const renderCell = (record, field) => {
    const isEditing = editingId === record._id;
    if (!isEditing) {
      switch (field) {
        case "datePaid":
          return moment(record.datePaid).format("MMMM Do YYYY");
        case "amountPaid":
          return toCommas(record.amountPaid);
        case "paymentMethod":
          return record.paymentMethod || "—";
        case "note":
          return record.note || "—";
        case "paidBy":
          return record.paidBy || "—";
        case "loggedBy":
          return record.loggedBy || "—";
        default:
          return "";
      }
    }

    const commonInputStyle = {
      width: "95%",
      padding: "6px 8px",
      borderRadius: 4,
      border: "1px solid #d1d5db",
    };

    switch (field) {
      case "datePaid":
        return (
          <input
            type="date"
            value={draft.datePaid || ""}
            onChange={(e) => setDraft({ ...draft, datePaid: e.target.value })}
            style={commonInputStyle}
          />
        );
      case "amountPaid":
        return (
          <input
            type="number"
            step="0.01"
            value={draft.amountPaid}
            onChange={(e) => setDraft({ ...draft, amountPaid: e.target.value })}
            style={commonInputStyle}
          />
        );
      case "paymentMethod":
        return (
          <select
            value={draft.paymentMethod || ""}
            onChange={(e) =>
              setDraft({ ...draft, paymentMethod: e.target.value })
            }
            style={{ ...commonInputStyle, paddingRight: 24 }}
          >
            <option value="" disabled>
              Select method
            </option>
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        );
      case "paidBy":
        return (
          <input
            type="text"
            value={draft.paidBy}
            onChange={(e) => setDraft({ ...draft, paidBy: e.target.value })}
            style={commonInputStyle}
          />
        );
      case "note":
        return (
          <input
            type="text"
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            style={commonInputStyle}
          />
        );
      case "loggedBy":
        return draft?.loggedBy || record.loggedBy || "—";
      default:
        return null;
    }
  };

  return (
    <div className="tabs">
      <div className="tab">
        <input type="checkbox" id="chck1" className="tab-toggle" />
        <label className="tab-label" htmlFor="chck1">
          Payment History{" "}
          <span className={styles.totalUnpaid}>{paymentRecords?.length}</span>
          <span className={styles.space}></span>
        </label>
        <div className="tab-content">
          <div>
            <table>
              <tbody>
                <tr>
                  <th>Date Paid</th>
                  <th>Amount Paid</th>
                  <th>Payment Method</th>
                  <th>Paid By</th>
                  <th>Logged By</th>
                  <th>Note</th>
                  {allowEditing && <th>Actions</th>}
                </tr>
                {paymentRecords?.map((record) => {
                  const isEditing = editingId === record._id;
                  return (
                    <tr key={record._id}>
                      <td>{renderCell(record, "datePaid")}</td>
                      <td>{renderCell(record, "amountPaid")}</td>
                      <td>{renderCell(record, "paymentMethod")}</td>
                      <td>{renderCell(record, "paidBy")}</td>
                      <td>{renderCell(record, "loggedBy")}</td>
                      <td>{renderCell(record, "note")}</td>
                      {allowEditing && (
                        <td style={{ minWidth: 120 }}>
                          {isEditing ? (
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                onClick={handleSave}
                                disabled={savingId === record._id}
                                style={{ padding: "4px 8px" }}
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                style={{ padding: "4px 8px" }}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(record)}
                              style={{ padding: "4px 8px" }}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
